import type { UnwrapNestedRefs } from '@vue/composition-api';
import { DateTime } from 'luxon';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import { nanoid } from 'nanoid';
import type { Channel } from 'pusher-js';
import { PusherEvent } from '@getaccept/pusher';
import type {
  Chat,
  ChatParticipant,
  Conversation,
  Message,
  MessageMetadata,
  ReadBy,
} from '@getaccept/dsr-shared';
import { ChatHelper, BaseChatCompositionApi, MessageType } from '@getaccept/dsr-shared';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { ChatService } from '../../../api/chat/chat.service';
import { pusherClient } from '../../../api/pusher/pusher-client';

export class ChatCompositionApi extends BaseChatCompositionApi {
  static async loadConversationList(chat: UnwrapNestedRefs<Chat>) {
    chat.error = false;
    chat.loadingConversationList = false;
    try {
      chat.conversations = await ChatService.loadConversationList();
    } catch {
      chat.error = true;
    } finally {
      chat.loadingConversationList = false;
    }
  }

  static async loadConversationDetails(chat: UnwrapNestedRefs<Chat>) {
    chat.error = false;
    chat.loadingConversationDetails = true;
    try {
      chat.conversation = await ChatService.loadConversationDetails(chat.conversation.id);
    } catch {
      chat.error = true;
    } finally {
      chat.loadingConversationDetails = false;
    }
  }

  static async createMessage(text: string, recipientId: string, chat: UnwrapNestedRefs<Chat>) {
    if (!chat.conversation) {
      return;
    }
    const metadata: MessageMetadata = chat.replyMessage
      ? {
          repliedTo: chat.replyMessage.id,
          mentions: [],
        }
      : undefined;
    const tempMessage: Partial<Message> = {
      id: nanoid(),
      text,
      owner: {
        recipientId,
      },
      metadata,
      createdAt: DateTime.now().toMillis(),
      isDraft: true,
      type: MessageType.Public,
      reads: [],
    };
    chat.conversation.messages.push(tempMessage as Message);
    try {
      const message: Message = await ChatService.addMessage(
        chat.conversation.id,
        text,
        chat.replyMessage?.id
      );
      this.addMessage(message, tempMessage.id, chat, true);
    } catch (e) {
      const errorMessage: Message = { ...tempMessage, error: true } as Message;
      this.addMessage(errorMessage, tempMessage.id, chat, true);
    }
  }

  static async setMessageReadBy(messageId: string, chat: UnwrapNestedRefs<Chat>) {
    await ChatService.setMessageReadBy(chat.conversation.id, messageId);
  }

  static async deleteMessage(messageId: string, chat: UnwrapNestedRefs<Chat>) {
    const message: Message = chat.conversation.messages.find(
      (message: Message) => message.id === messageId
    );
    this.addMessage(
      {
        ...message,
        text: `${t('deleting-message')}...`,
        isDraft: true,
      },
      messageId,
      chat,
      true
    );
    const success: boolean = await ChatService.deleteMessage(chat.conversation.id, messageId);
    if (success) {
      this.addMessage(
        {
          ...message,
          text: '',
          metadata: undefined,
          deletedAt: DateTime.now().toMillis(),
          isDraft: false,
        },
        messageId,
        chat,
        true
      );
    } else {
      this.addMessage(message, messageId, chat, true);
    }
  }

  static addMessageCreatedListener(channel: Channel, chat: UnwrapNestedRefs<Chat>): void {
    pusherClient.listen(
      channel,
      PusherEvent.MessageCreated,
      (event: { conversation: Partial<Conversation>; message: Message }) => {
        if (!event.message) {
          return;
        }
        if (chat.conversation.id !== event.conversation.id) {
          return;
        }
        this.addMessage(event.message, event.message.id, chat, false);
      }
    );
  }

  static addTypingStatusListener(
    channel: Channel,
    recipients: Recipient[],
    user: User,
    chat: UnwrapNestedRefs<Chat>
  ): void {
    pusherClient.listen(
      channel,
      PusherEvent.Typing,
      (event: { conversation: Partial<Conversation>; participant: Partial<ChatParticipant> }) => {
        chat.typing = [
          ...chat.typing,
          {
            conversationId: event.conversation.id,
            participant: this.mapChatParticipant(event.participant, recipients, user),
          },
        ];
      }
    );
    pusherClient.listen(
      channel,
      PusherEvent.StoppedTyping,
      (event: { conversation: Partial<Conversation>; participant: Partial<ChatParticipant> }) => {
        chat.typing = chat.typing.filter(({ conversationId, participant }) => {
          const isSameConversation: boolean = conversationId === event.conversation.id;
          const isSameParticipant: boolean = ChatHelper.isSameParticipant(
            event.participant,
            participant
          );
          return !(isSameConversation && isSameParticipant);
        });
      }
    );
  }

  static addMessageReadListener(channel: Channel, chat: UnwrapNestedRefs<Chat>): void {
    pusherClient.listen(channel, PusherEvent.MessageRead, event => {
      if (chat.conversation.id !== event.conversation.id) {
        return;
      }
      const message: Message = chat.conversation.messages.find(
        ({ id }: Message) => id === event.message.id
      );
      const read: ReadBy = {
        timestamp: DateTime.now().toMillis(),
        participant: { recipientId: event.recipientId, userId: event.userId },
      };
      this.addMessage({ ...message, reads: [...message.reads, read] }, message.id, chat, true);
    });
  }

  static addMessageDeletedListener(channel: Channel, chat: UnwrapNestedRefs<Chat>): void {
    pusherClient.listen(channel, PusherEvent.MessageDeleted, event => {
      if (chat.conversation.id !== event.conversation.id) {
        return;
      }
      const message: Message = chat.conversation.messages.find(
        ({ id }: Message) => id === event.message.id
      );
      this.addMessage(
        { ...message, deletedAt: DateTime.now().toMillis(), text: '', metadata: undefined },
        message.id,
        chat,
        true
      );
    });
  }
}
